import CategoryScoreCardHero from './CategoryScoreCardHero';
import { DATA_SOURCES } from '@/config/constants';
import DownloadTimeSeriesButton from 'components/Button/DownloadTimeSeriesButton';
import EntityBreadcrumbs from '../shared/entity/EntityBreadcrumbs';
import EntityChartDownload from '../shared/entity/EntityChartDownload';
import EntityGraphNav from '../shared/entity/EntityGraphNav';
import EntityPage from '../shared/entity/EntityPage';
import EntityRelatedTable from '../shared/entity/EntityRelatedTable';
import EntityTabs, { EntityTab } from '../shared/entity/EntityTabs';
import EntityTimeSeriesTab from '../shared/entity/EntityTimeSeriesTab';
import EntityTopPosts from '../shared/entity/EntityTopPosts';
import EntityTopTerms from '../shared/entity/EntityTopTerms';
import InstagramBreadCrumbs from 'components/Breadcrumbs/InstagramBreadCrumbs';
import InstagramGraphNav from 'components/DataVis/GraphNav/InstagramGraphNav';
import { INSTAGRAM_METADATA_BY_KEY as M } from 'utils/metrics';
import {
    InstagramRelatedBrandsSectionTable,
    InstagramRelatedCategoriesSectionTable,
} from 'components/DataVis/InstagramRelatedCategoriesTable';
import InstagramTimeSeries from 'components/DataVis/InstagramTimeSeries';
import Stack from 'components/Stack';
import { SUMMARY_SPEC, scoreCardSummary } from './categorySummary';
import { TopInstagramHashtagsSection } from 'components/TopKeywordsSection';
import TopPostsSection from 'components/Posts/Instagram/TopPostsSection/CategoryTopPostsSection';
import useInstagramEntity, {
    useInstagramSiblings,
} from '../shared/entity/useInstagramEntity';
import useParentCategory from './useParentCategory';
import { useTrackDepthView } from 'utils/hooks/useTrackEntityView';
import WeeklyEntityChart, {
    weeklySeries,
} from '../shared/entity/WeeklyEntityChart';

const HERO_METRICS = [
    M.non_cum_YoY_rolling,
    M.non_cum_QoQ_rolling,
    M.non_cum_MoM_rolling,
    M.non_cum_WoW,
];

function InstagramCategoryThreePage({ categoryId, controller, tabs }) {
    const page = useInstagramEntity({
        entityType: 'categories',
        entityId: categoryId,
        controller,
        coverage: 'hintCat1',
    });
    const {
        entity: category,
        details,
        row,
        name,
        country,
        week,
        year,
        rowQuery,
    } = page;

    const parentCategory = useParentCategory(page);
    const { siblings, ready } = useInstagramSiblings({
        parentCategory,
        country,
        controller,
    });
    const summary = scoreCardSummary({
        entity: category,
        parentCategory,
        siblings,
        ready,
        spec: SUMMARY_SPEC[DATA_SOURCES.INSTAGRAM],
        currentPage: {
            id: categoryId,
            name,
            non_cum_increase_YoY_rolling: row?.non_cum_increase_YoY_rolling,
        },
    });

    useTrackDepthView(DATA_SOURCES.INSTAGRAM, {
        depth: details?.depth,
        name,
        country,
    });

    const series = weeklySeries({
        page,
        color: page.theme.instagram_orange,
        type: 'likes',
        metricLabel: 'likes',
    });

    return (
        <EntityPage
            page={page}
            breadcrumbs={<EntityBreadcrumbs as={InstagramBreadCrumbs} />}
            sourceTabs={tabs}
            space="32px"
            tabs={
                <EntityTabs>
                    <EntityTab name="Related Categories">
                        <EntityRelatedTable
                            as={InstagramRelatedCategoriesSectionTable}
                            subject="categories"
                            icon="affinity"
                            title="Related Categories"
                            exportType="related-category"
                            exportEntity={name || 'trend'}
                            sortParamKey="relatedSort"
                        />
                    </EntityTab>
                    <EntityTab name="Related Brands">
                        <EntityRelatedTable
                            as={InstagramRelatedBrandsSectionTable}
                            subject="brands"
                            icon="brand"
                            title="Related Brands"
                            exportType="related-brand"
                            exportEntity={name || 'trend'}
                            sortParamKey="brandSort"
                        />
                    </EntityTab>
                    <EntityTab name="Time Series">
                        <EntityTimeSeriesTab
                            title="Time Series"
                            subtitle="How is the trend performing?"
                            tooltipKey="instagramTimeSeriesTitleHelp"
                            download={
                                <EntityChartDownload
                                    as={DownloadTimeSeriesButton}
                                    data={series}
                                    className="ml-auto"
                                    mixpanelMeta={{
                                        contentKey: 'InstagramTimeSeriesChart',
                                    }}
                                />
                            }
                        >
                            <WeeklyEntityChart
                                as={InstagramTimeSeries}
                                data={series}
                                cluster={row?.cluster}
                                isLoading={rowQuery.isLoading}
                            />
                        </EntityTimeSeriesTab>
                    </EntityTab>
                </EntityTabs>
            }
        >
            <Stack $space="16px">
                <CategoryScoreCardHero
                    categoryId={categoryId}
                    name={name}
                    image={details?.image}
                    isLoading={rowQuery.isLoading}
                    volumeLabel="Likes"
                    volume={row?.non_cum_avg_weekly_likes}
                    metrics={row || {}}
                    metricsList={HERO_METRICS}
                    defaultMetric={M.non_cum_YoY_rolling}
                    attributionProps={{ week, year }}
                    // The source name is already the section's context.
                    cluster={row?.cluster?.replace('instagram', '').trim()}
                    {...summary}
                />
                <EntityGraphNav as={InstagramGraphNav} />
                <EntityTopTerms as={TopInstagramHashtagsSection} />
                <EntityTopPosts as={TopPostsSection} />
            </Stack>
        </EntityPage>
    );
}

InstagramCategoryThreePage.displayName = 'InstagramCategoryThree';

export default InstagramCategoryThreePage;
